import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import { RiskAssessmentResultControlType } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import type { UpdateControlledRiskInternalAuditResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type UpdateRiskInternalAuditResultInput = {
  Id: string;
  Rating?: number | null;
  Impact?: number | null;
  Likelihood?: number | null;
  Rationale?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
};

const mapTrpcResponseToGraphQL =
  (): UpdateControlledRiskInternalAuditResultMutation => ({
    update_risk_controlled_internal_audit_result: {
      __typename: 'risk_controlled_internal_audit_result_mutation_response',
      affected_rows: 1,
    },
  });

export const useUpdateControlledRiskInternalAuditResultTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.internalAuditResult.updateRisk.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.internalAuditResult.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.frontend.assessment.internalAuditResultCount.queryKey(),
          }),
          // COI-471 parity: Apollo twin also evicts `internal_audit_report`.
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.internalAuditReport.pathKey(),
          }),
        ]);
      },
    }),
  });

  return {
    updateControlledRiskInternalAuditResult: async (
      variables: UpdateRiskInternalAuditResultInput
    ): Promise<UpdateControlledRiskInternalAuditResultMutation> => {
      await mutation.mutateAsync({
        ...variables,
        ControlType: RiskAssessmentResultControlType.Controlled,
      });

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
